import { FC, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Alert, Button } from '@mui/material';
import {
  IconChevronLeft,
  IconExternalLink,
  IconCalendar,
  IconClipboardList,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import useGetPartnerBoats from '../../../hooks/useGetPartnerBoats';
import useGetPartnerTrips from '../../../hooks/useGetPartnerTrips';
import useGetPartnerBookings from '../../../hooks/useGetPartnerBookings';
import { getErrorMessage } from '../../../utils/errorUtils';
import { format } from 'date-fns';
import { getBookingStatus } from '../../../constants/bookingStatus';

const BoatDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const boatId = parseInt(id || '0', 10);

  const {
    boats,
    isLoading: boatsLoading,
    isError: boatsError,
    error: boatsErrorDetails,
  } = useGetPartnerBoats();
  const {
    trips,
    isLoading: tripsLoading,
    isError: tripsError,
    error: tripsErrorDetails,
  } = useGetPartnerTrips();
  const {
    bookings,
    isLoading: bookingsLoading,
    isError: bookingsError,
    error: bookingsErrorDetails,
  } = useGetPartnerBookings();

  const isLoading = boatsLoading || tripsLoading || bookingsLoading;
  const hasError = boatsError || tripsError || bookingsError;

  // Sort states for trips table
  type SortField = 'embarkationDate' | 'disembarkationDate';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('embarkationDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Find the boat
  const boat = useMemo(() => {
    return boats.find(b => b.boatId === boatId);
  }, [boats, boatId]);

  // Filter trips for this boat
  const boatTrips = useMemo(() => {
    return trips.filter(trip => trip.boatId === boatId);
  }, [trips, boatId]);

  // Filter bookings for this boat
  const boatBookings = useMemo(() => {
    return bookings.filter(booking => booking.boatId === boatId);
  }, [bookings, boatId]);

  // Sort trips by selected field and direction
  const sortedTrips = useMemo(() => {
    const sorted = [...boatTrips];
    sorted.sort((a, b) => {
      let aValue: number;
      let bValue: number;

      if (sortField === 'embarkationDate') {
        aValue = new Date(a.embarkationDate).getTime();
        bValue = new Date(b.embarkationDate).getTime();
      } else {
        aValue = new Date(a.disembarkationDate).getTime();
        bValue = new Date(b.disembarkationDate).getTime();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [boatTrips, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortButton: FC<{ field: SortField; children: React.ReactNode }> = ({
    field,
    children,
  }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-gray-900"
    >
      {children}
      {sortField === field &&
        (sortDirection === 'asc' ? (
          <IconChevronUp className="h-4 w-4" />
        ) : (
          <IconChevronDown className="h-4 w-4" />
        ))}
    </button>
  );

  // Sort bookings by date (most recent first)
  const sortedBookings = useMemo(() => {
    return [...boatBookings].sort(
      (a, b) =>
        new Date(b.embarkationDate).getTime() -
        new Date(a.embarkationDate).getTime(),
    );
  }, [boatBookings]);

  const getStatusBadge = (status: number) => {
    const statusInfo = getBookingStatus(status);
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}
      >
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (hasError) {
    const errorMessage =
      getErrorMessage(boatsErrorDetails) ||
      getErrorMessage(tripsErrorDetails) ||
      getErrorMessage(bookingsErrorDetails) ||
      'Unknown error';

    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading boat data</div>
          <div className="mt-2 text-sm">{errorMessage}</div>
        </Alert>
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/boats')}
        >
          Back to Boats
        </Button>
      </div>
    );
  }

  if (!boat) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="warning" className="mb-4">
          Boat not found
        </Alert>
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/boats')}
        >
          Back to Boats
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/boats')}
          className="mb-4"
        >
          Back to Boats
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{boat.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              View boat details, trips, and bookings
            </p>
          </div>
          <a
            href={`/boat/${boat.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <IconExternalLink className="h-4 w-4" />
            View Public Page
          </a>
        </div>
      </div>

      {/* Boat Information */}
      <div className="mb-6 grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            Boat Information
          </h2>
          <dl className="mt-4 space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Boat ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{boat.boatId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{boat.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Slug</dt>
              <dd className="mt-1 text-sm text-gray-900">{boat.slug}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Currency</dt>
              <dd className="mt-1 text-sm text-gray-900">{boat.currency}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Statistics</h2>
          <dl className="mt-4 space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Trips</dt>
              <dd className="mt-1 text-sm text-gray-900">{boatTrips.length}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Total Bookings
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {boatBookings.length}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Confirmed Bookings
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {boatBookings.filter(b => b.bookingStatus === 1).length}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Associated Trips */}
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <IconCalendar className="h-5 w-5" />
            Associated Trips ({boatTrips.length})
          </h2>
        </div>
        {sortedTrips.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <p className="text-gray-500">No trips found for this boat</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Trip ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="embarkationDate">Embarkation</SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="disembarkationDate">
                      Disembarkation
                    </SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Dives
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedTrips.map(trip => (
                  <tr key={trip.tripId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {trip.tripId}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {trip.routeName || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(new Date(trip.embarkationDate), 'MMM dd, yyyy')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(
                        new Date(trip.disembarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {trip.diveCount || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <button
                        onClick={() =>
                          navigate(`/partner/trips/${trip.tripId}`)
                        }
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bookings for this Boat */}
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <IconClipboardList className="h-5 w-5" />
            Bookings ({boatBookings.length})
          </h2>
        </div>
        {sortedBookings.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <p className="text-gray-500">No bookings found for this boat</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Booking ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Embarkation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Guests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedBookings.map(booking => (
                  <tr key={booking.bookingId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {booking.bookingId}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {booking.routeName || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(
                        new Date(booking.embarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {booking.guestCount}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {getStatusBadge(booking.bookingStatus)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <button
                        onClick={() =>
                          navigate(`/partner/bookings/${booking.bookingId}`)
                        }
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoatDetail;
